import qs from 'qs';
import { apiClientWithApi } from '../utils/http';
import {
  loadInboundTaskTypeDictionary,
  loadOutboundTaskTypeDictionary,
} from './taskTypeDictionary';
import {
  TaskDispensarization,
  TaskForm,
  TaskListResponse,
  TaskQuery,
  TaskResultDictionary,
  TaskSlot,
  TaskStateDictionary,
} from '../models/Task/Task';

export type { TaskQuery } from '../models/Task/Task';

const serializeRepeatedParams = (params: Record<string, unknown>) =>
  qs.stringify(params, { arrayFormat: 'repeat' });

export const buildTaskListParams = (query: TaskQuery) => ({
  'Filter.From': query.from,
  'Filter.To': query.to,
  'Filter.ClinicIds': query.clinicIds,
  'Filter.States': query.states,
  'Filter.AttemptStates': query.attemptStates,
  'Filter.Result': query.result,
  'Filter.Types': query.types,
  'Filter.TextSearch': query.textSearch,
  'Filter.AttemptFrom': query.attemptFrom,
  'Filter.AttemptTo': query.attemptTo,
  ...(query.formTemplateIds
    ? { 'Filter.FormTemplateIds': query.formTemplateIds }
    : {}),
  ...(query.formBatchIds ? { 'Filter.FormBatchIds': query.formBatchIds } : {}),
  ...(query.dispensarizationBatchIds
    ? { 'Filter.DispensarizationBatchIds': query.dispensarizationBatchIds }
    : {}),
  PageIndex: query.pageIndex,
  PageSize: query.pageSize,
  sortQuery: query.sortQuery,
});

export const buildTaskReportParams = (query: TaskQuery) => ({
  From: query.from,
  To: query.to,
  ClinicIds: query.clinicIds,
  States: query.states,
  AttemptStates: query.attemptStates,
  Result: query.result,
  Types: query.types,
  TextSearch: query.textSearch,
  AttemptFrom: query.attemptFrom,
  AttemptTo: query.attemptTo,
  ...(query.formTemplateIds ? { FormTemplateIds: query.formTemplateIds } : {}),
  ...(query.formBatchIds ? { FormBatchIds: query.formBatchIds } : {}),
  ...(query.dispensarizationBatchIds
    ? { DispensarizationBatchIds: query.dispensarizationBatchIds }
    : {}),
});

const getTaskList = async (path: string, query: TaskQuery) => {
  const response = await apiClientWithApi.get<TaskListResponse>(path, {
    params: buildTaskListParams(query),
    paramsSerializer: serializeRepeatedParams,
  });

  return response.data;
};

const getTaskReport = async (path: string, query: TaskQuery) => {
  const response = await apiClientWithApi.get<Blob>(path, {
    params: buildTaskReportParams(query),
    paramsSerializer: serializeRepeatedParams,
    responseType: 'blob',
  });

  return response.data;
};

export const taskApi = {
  getInboundTasks: (query: TaskQuery) => getTaskList('task/inbound', query),

  getOutboundTasks: (query: TaskQuery) => getTaskList('task/outbound', query),

  getTaskStates: async (): Promise<TaskStateDictionary> => {
    const response =
      await apiClientWithApi.get<TaskStateDictionary>('task/state');
    return response.data;
  },

  getTaskResults: async (): Promise<TaskResultDictionary> => {
    const response =
      await apiClientWithApi.get<TaskResultDictionary>('task/result');
    return response.data;
  },

  getInboundTaskTypes: loadInboundTaskTypeDictionary,

  getOutboundTaskTypes: loadOutboundTaskTypeDictionary,

  getInboundTaskReport: (query: TaskQuery) =>
    getTaskReport('task/inbound/report', query),

  getOutboundTaskReport: (query: TaskQuery) =>
    getTaskReport('task/outbound/report', query),

  getTaskSlot: async (taskId: string): Promise<TaskSlot> => {
    const response = await apiClientWithApi.get<TaskSlot>(
      `task/slot/${taskId}`,
    );
    return response.data;
  },

  getTaskDispensarization: async (
    taskId: string,
  ): Promise<TaskDispensarization> => {
    const response = await apiClientWithApi.get<TaskDispensarization>(
      `task/dispensarization/${taskId}`,
    );
    return response.data;
  },

  getTaskForm: async (taskId: string): Promise<TaskForm> => {
    const response = await apiClientWithApi.get<TaskForm>(
      `task/form/${taskId}`,
    );
    return response.data;
  },
};
